import { useState, useMemo } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import type { TodoListCollaboratorResponse } from '@/lib/api';

interface ShareListDialogProps {
  open: boolean;
  onClose: () => void;
  collaborators: TodoListCollaboratorResponse[];
  onAdd: (input: { username: string }) => Promise<unknown>;
  onRemove: (userId: string) => Promise<unknown>;
}

export function ShareListDialog({
  open,
  onClose,
  collaborators,
  onAdd,
  onRemove,
}: ShareListDialogProps) {
  const [username, setUsername] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [pendingAdds, setPendingAdds] = useState<TodoListCollaboratorResponse[]>([]);

  const allCollaborators = useMemo(() => {
    const ids = new Set(collaborators.map((c) => c.userId));
    return [...collaborators, ...pendingAdds.filter((p) => !ids.has(p.userId))];
  }, [collaborators, pendingAdds]);

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      onClose();
      setUsername('');
      setError(null);
      setPendingAdds([]);
    }
  }

  async function handleAdd() {
    const trimmed = username.trim();
    if (!trimmed) return;

    setAdding(true);
    setError(null);
    try {
      const result = await onAdd({ username: trimmed });
      if (result && typeof result === 'object' && 'userId' in result) {
        setPendingAdds((prev) => [...prev, result as TodoListCollaboratorResponse]);
      }
      setUsername('');
    } catch {
      setError(`Could not add "${trimmed}". User may not exist.`);
    } finally {
      setAdding(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Share List</DialogTitle>
          <DialogDescription>
            Add users by username. Use the assign buttons on sections and items to give them edit
            access.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Add by username</Label>
            <div className="flex gap-2">
              <Input
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  setError(null);
                }}
                placeholder="Enter username and press Enter"
                disabled={adding}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAdd();
                  }
                }}
              />
              <Button variant="outline" onClick={handleAdd} disabled={adding || !username.trim()}>
                Add
              </Button>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          {allCollaborators.length > 0 && (
            <div className="space-y-2">
              <Label>Collaborators</Label>
              <div className="space-y-2">
                {allCollaborators.map((c) => (
                  <div key={c.userId} className="flex items-center gap-2 p-2 rounded-md border">
                    <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
                      {c.username[0].toUpperCase()}
                    </div>
                    <span className="text-sm flex-1">{c.username}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => onRemove(c.userId)}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
